/**
 * Responsive app frame — "Aurora" (D49):
 *  - phone (< 640px): top bar + floating bottom tab bar (Home, Swap, Activity, Vault, Settings)
 *  - tablet and desktop (≥ 640px): top bar with round icon buttons, the active one in lime
 */
import type { SystemMessage } from "@custos/api-client";
import { Logo, NetworkBadge, SystemMessages, cn } from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  History,
  Home,
  LogOut,
  MessageCircle,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { api, queryClient } from "../lib/api";
import { qk, useChatUnread, useConfig, useMessages } from "../lib/queries";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  tab?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: Home, tab: true },
  { to: "/deposit", label: "Deposit", icon: ArrowDownLeft },
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpRight },
  { to: "/swap", label: "Swap", icon: ArrowLeftRight, tab: true },
  { to: "/activity", label: "Activity", icon: History, tab: true },
  { to: "/vault", label: "Vault", icon: ShieldCheck, tab: true },
  { to: "/settings", label: "Settings", icon: Settings, tab: true },
];

export function useLogout() {
  const navigate = useNavigate();
  return async () => {
    await api.logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
}

function Badge() {
  const config = useConfig();
  return config.data ? <NetworkBadge label={config.data.badge} /> : null;
}

/** Messages from the team, above every page (D78). Closing one is remembered on all devices. */
function TeamMessages() {
  const messages = useMessages();
  const dismiss = (id: string) => {
    queryClient.setQueryData<SystemMessage[]>(qk.messages, (list) => list?.filter((m) => m.id !== id));
    api.dismissMessage(id).catch(() => void queryClient.invalidateQueries({ queryKey: qk.messages }));
  };
  return <SystemMessages className="mb-4" messages={messages.data ?? []} onDismiss={dismiss} />;
}

/** Round chat button (D79): above the tab bar on phones, bottom-right otherwise; lime dot = unread reply. */
/** Phone width, live (the chat button sits in the top bar there instead of floating over content, D80). */
function useIsPhone(): boolean {
  const query = "(max-width: 639px)";
  const [phone, setPhone] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setPhone(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return phone;
}

function ChatButton({ inBar = false }: { inBar?: boolean }) {
  const unread = useChatUnread();
  const location = useLocation();
  if (location.pathname === "/help") return null;
  const n = unread.data ?? 0;
  return (
    <NavLink
      to="/help"
      aria-label={n > 0 ? `Help chat, ${n} unread` : "Help chat"}
      title="Help chat"
      data-testid="chat-button"
      className={
        inBar
          ? "relative inline-flex size-11 items-center justify-center rounded-full bg-soft text-fg hover:bg-hover"
          : "fixed right-6 bottom-6 z-30 inline-flex size-14 items-center justify-center rounded-full bg-fg text-bg shadow-2xl transition-transform hover:scale-105"
      }
    >
      <MessageCircle className={inBar ? "size-5" : "size-6"} aria-hidden />
      {n > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-on-primary"
          data-testid="chat-unread"
        >
          {n}
        </span>
      )}
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const logout = useLogout();
  const location = useLocation();
  const phone = useIsPhone();

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Tablet + desktop: top bar with round icon navigation */}
      <header className="sticky top-0 z-30 hidden bg-bg/70 backdrop-blur-xl sm:block">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4 lg:px-10">
          <Logo compact className="lg:hidden" />
          <Logo className="hidden lg:inline-flex" />
          <nav
            aria-label="Main navigation"
            data-testid="topnav"
            className="mx-auto flex items-center gap-1.5 lg:gap-2"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={item.label}
                aria-label={item.label}
                className="group flex flex-col items-center gap-1 lg:w-[4.25rem]"
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        "inline-flex size-11 items-center justify-center rounded-full transition-colors duration-200 lg:size-12",
                        isActive
                          ? "animate-pop bg-primary text-on-primary"
                          : "bg-soft text-fg group-hover:bg-hover",
                      )}
                    >
                      <item.icon className="size-5" aria-hidden />
                    </span>
                    <span
                      className={cn("hidden text-[11px] lg:block", isActive ? "text-fg" : "text-muted")}
                      aria-hidden
                    >
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Badge />
            <button
              type="button"
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              className="inline-flex size-11 items-center justify-center rounded-full bg-soft text-fg hover:bg-hover lg:size-12"
            >
              <LogOut className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </header>

      {/* Phone top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between bg-bg/70 px-4 py-3 backdrop-blur-xl sm:hidden">
        <Logo />
        <div className="flex items-center gap-2">
          <Badge />
          {phone && <ChatButton inBar />}
        </div>
      </header>

      <main
        key={location.pathname}
        className="mx-auto w-full max-w-7xl flex-1 animate-fade px-4 pt-3 pb-32 sm:px-6 sm:pt-4 sm:pb-12 lg:px-10"
      >
        <TeamMessages />
        {children}
      </main>

      {!phone && <ChatButton />}

      {/* Phone floating bottom tab bar */}
      <nav
        aria-label="Tabs"
        data-testid="tabbar"
        className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-30 grid grid-cols-5 gap-1 rounded-full border border-line bg-surface/95 p-1.5 shadow-2xl backdrop-blur-xl sm:hidden"
      >
        {NAV.filter((i) => i.tab).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              cn(
                "flex min-h-13 flex-col items-center justify-center gap-0.5 rounded-full text-[10px] font-medium transition-colors duration-200",
                isActive ? "animate-pop bg-primary text-on-primary" : "text-muted",
              )
            }
          >
            <item.icon className="size-5" aria-hidden />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
      <div className="min-w-0">
        <h1 className="font-heading text-[28px] leading-tight font-medium text-fg sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
